import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const BG = "https://images.unsplash.com/photo-1483366774565-c783b9f70e2c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", organizationName: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = mode === "login"
        ? await api.post("/auth/login", { email: form.email, password: form.password })
        : await api.post("/auth/register", form);
      login(data);
      navigate(data.user.role === "platform_admin" ? "/admin" : "/projects");
    } catch (err) { setError(errMsg(err)); }
    setBusy(false);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 p-12 flex flex-col justify-between h-full text-white">
          <div className="flex items-center gap-2.5 self-start">
            <img src="/logo-icon.png" alt="Ganttastic" className="h-12 w-12" />
            <div className="font-display font-black tracking-tighter text-2xl">Ganttastic<span className="text-[#5B93F0]">.app</span></div>
          </div>
          <div>
            <h1 className="font-display font-black text-5xl tracking-tighter leading-none">
              Project management<br />just got <span className="text-[#5B93F0]">Ganttastic.</span>
            </h1>
            <p className="mt-6 text-white/70 max-w-md text-sm">
              The clarity you've been missing, without the usual weight that comes with it. Plans stay honest, teams stay aligned, and progress finally feels possible — even when things get complicated.
            </p>
          </div>
          <div className="overline-label text-white/50">ganttastic.app · Enterprise Delivery</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4">
          <div>
            <div className="overline-label mb-1">{mode === "login" ? "Sign in" : "Create workspace"}</div>
            <h2 className="font-display font-black text-3xl tracking-tighter">
              {mode === "login" ? "Welcome back" : "New tenant"}
            </h2>
            {mode === "register" && (
              <p className="text-xs text-muted-foreground mt-1">Creates a new isolated tenant pre-loaded with the Northern Health QC4 demo project.</p>
            )}
          </div>
          {mode === "register" && (
            <>
              <div><Label>Your name</Label><Input data-testid="register-name-input" value={form.name} onChange={set("name")} required /></div>
              <div><Label>Organization name</Label><Input data-testid="register-org-input" value={form.organizationName} onChange={set("organizationName")} required /></div>
            </>
          )}
          <div><Label>Email</Label><Input data-testid="login-email-input" type="email" value={form.email} onChange={set("email")} required /></div>
          <div><Label>Password</Label><Input data-testid="login-password-input" type="password" value={form.password} onChange={set("password")} required /></div>
          {error && <div data-testid="auth-error" className="text-sm text-red-600 border border-red-300 bg-red-50 px-3 py-2">{error}</div>}
          <Button data-testid="login-submit-btn" type="submit" disabled={busy} className="w-full rounded-none bg-[#1E6FE8] hover:bg-[#1557C6]">
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create tenant & sign in"}
          </Button>
          <button type="button" data-testid="toggle-auth-mode-btn" className="text-xs underline text-muted-foreground"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}>
            {mode === "login" ? "New organization? Create a tenant workspace" : "Already have an account? Sign in"}
          </button>
          <div className="border border-border p-3 text-xs text-muted-foreground space-y-1">
            <div className="overline-label">Demo accounts</div>
            <div>Tenant: demo@northernhealth.org / Demo#2026</div>
            <div>Platform Admin: platform@pmsaas.io / Platform#2026</div>
          </div>
        </form>
      </div>
    </div>
  );
}
